import { DateRangeFilter } from "@/components/filters/DateRangeFilter";
import { DocumentTypeFilter } from "@/components/filters/DocumentTypeFilter";
import { ProNumberFilter } from "@/components/filters/ProNumberFilter";
import { ShipmentIdFilter } from "@/components/filters/ShipmentIdFilter";
import { AddFilterButton, type FilterOption } from "@/components/shared/AddFilterButton";
import { ClearFilterButton } from "@/components/shared/ClearFilterButton";
import type { ActiveFilter, DateRange, FilterType } from "@/hooks/useFilters";
import { useState } from "react";

/**
 * Available filter options for the "Add Filter" dropdown
 */
const FILTER_OPTIONS: FilterOption<FilterType>[] = [
  { value: "proNumber", label: "PRO Number" },
  { value: "shipmentId", label: "Shipment ID" },
  { value: "documentType", label: "Document Type" },
  { value: "dateRange", label: "Date Range" },
];

interface FilterSystemProps {
  /** Current active filters */
  filters: ActiveFilter[];
  /** Callback when a filter is added */
  onAddFilter: (filter: ActiveFilter) => void;
  /** Callback when a filter is removed */
  onRemoveFilter: (filterType: FilterType) => void;
  /** Callback when a filter value is updated */
  onUpdateFilter: (filterType: FilterType, value: string | string[] | DateRange) => void;
  /** Callback to clear all filters */
  onClearFilters: () => void;
}

/**
 * FilterSystem component - Container for the dynamic filter system
 *
 * Displays:
 * - Active filters as removable chips
 * - "Add Filter" button with dropdown to add new filters
 * - "Clear All" button to reset all filters (when filters are active)
 *
 * Filters are applied with AND logic (documents must match all filters).
 */
export function FilterSystem({
  filters,
  onAddFilter,
  onRemoveFilter,
  onUpdateFilter,
  onClearFilters,
}: FilterSystemProps) {
  // Track which filter was just added (to open it immediately)
  const [newlyAddedFilter, setNewlyAddedFilter] = useState<FilterType | null>(null);

  // Get filter types that are already active (to disable in dropdown)
  const activeFilterTypes = new Set(filters.map((f) => f.type));

  // Filter options that haven't been added yet
  const availableOptions = FILTER_OPTIONS.filter((option) => !activeFilterTypes.has(option.value));

  const handleAddFilter = (option?: FilterOption<FilterType>) => {
    if (!option) return;

    // Create default filter based on type
    let defaultValue: string | string[] | DateRange;
    let displayValue: string;

    switch (option.value) {
      case "proNumber":
        defaultValue = "";
        displayValue = "All";
        break;
      case "shipmentId":
        defaultValue = "";
        displayValue = "All";
        break;
      case "documentType":
        defaultValue = [];
        displayValue = "All";
        break;
      case "dateRange":
        defaultValue = { startDate: null, endDate: null };
        displayValue = "All";
        break;
      default:
        return;
    }

    const newFilter: ActiveFilter = {
      type: option.value,
      value: defaultValue,
      displayValue,
    };

    onAddFilter(newFilter);
    setNewlyAddedFilter(option.value);

    // Clear the newly added flag after the component has had time to mount and open
    // Using a longer delay to ensure async components (like DocumentTypeFilter) have loaded
    setTimeout(() => setNewlyAddedFilter(null), 500);
  };

  const handleRemoveFilter = (filterType: FilterType) => {
    onRemoveFilter(filterType);
  };

  // Get filters by type (using explicit variables to avoid non-null assertions)
  const proNumberFilter = filters.find((f) => f.type === "proNumber");
  const shipmentIdFilter = filters.find((f) => f.type === "shipmentId");
  const documentTypeFilter = filters.find((f) => f.type === "documentType");
  const dateRangeFilter = filters.find((f) => f.type === "dateRange");

  const hasFilters = filters.length > 0;

  return (
    <div className="flex flex-wrap items-center gap-3 p-3 bg-gray-50/50 dark:bg-[#192233]/30">
      {/* PRO Number Filter */}
      {proNumberFilter && (
        <ProNumberFilter
          value={proNumberFilter.value as string}
          onChange={(value) => onUpdateFilter("proNumber", value)}
          onRemove={() => handleRemoveFilter("proNumber")}
          initialOpen={newlyAddedFilter === "proNumber"}
        />
      )}

      {/* Shipment ID Filter */}
      {shipmentIdFilter && (
        <ShipmentIdFilter
          value={shipmentIdFilter.value as string}
          onChange={(value) => onUpdateFilter("shipmentId", value)}
          onRemove={() => handleRemoveFilter("shipmentId")}
          initialOpen={newlyAddedFilter === "shipmentId"}
        />
      )}

      {/* Document Type Filter */}
      {documentTypeFilter && (
        <DocumentTypeFilter
          value={documentTypeFilter.value as string[]}
          onChange={(types) => onUpdateFilter("documentType", types)}
          onRemove={() => handleRemoveFilter("documentType")}
          initialOpen={newlyAddedFilter === "documentType"}
        />
      )}

      {/* Date Range Filter */}
      {dateRangeFilter && (
        <DateRangeFilter
          value={dateRangeFilter.value as DateRange}
          onChange={(range) => onUpdateFilter("dateRange", range)}
          onRemove={() => handleRemoveFilter("dateRange")}
          initialOpen={newlyAddedFilter === "dateRange"}
        />
      )}

      {/* Add Filter Button - only show if there are available options */}
      {availableOptions.length > 0 && (
        <AddFilterButton options={availableOptions} onClose={handleAddFilter} />
      )}

      {/* Clear All Button - only show if there are active filters */}
      {hasFilters && <ClearFilterButton onClick={onClearFilters} />}
    </div>
  );
}
